/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Fragment, type MouseEvent as ReactMouseEvent, useEffect, useRef, useState } from 'react';

import { LAYOUT2 } from '@/Client/Styles';
import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT, withOpacity } from '@kaiizen/kai-shiki/core';
import { MoreVerticalIcon } from '@kaiizen/kai-shiki/react';
import { KEBAB_TRIGGER_REVEAL_STYLE } from './rowKebabMenuStyles';

const MENU_WRAP_STYLE = css`
  label: MENU_WRAP_STYLE;
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  flex-shrink: 0;
  display: inline-flex;
`;

/** `:focus-visible` (in KEBAB_TRIGGER_REVEAL_STYLE) is what keeps the trigger reachable without a mouse. */
const MENU_TRIGGER_STYLE = css`
  label: MENU_TRIGGER_STYLE;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${COLORS.textTertiary};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 120ms ease, background 120ms ease;

  &:hover,
  &[aria-expanded='true'] {
    color: ${COLORS.ink};
    background: ${COLORS.stroke};
  }

  &:focus-visible { outline: 2px solid ${COLORS.ink}; outline-offset: 1px; }
`;

/** A row whose other controls already hold its gutter open keeps the kebab in view permanently. */
const MENU_TRIGGER_ALWAYS_VISIBLE_STYLE = css`
  label: MENU_TRIGGER_ALWAYS_VISIBLE_STYLE;
  visibility: visible;
`;

const MENU_STYLE = css`
  label: MENU_STYLE;
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 20;
  min-width: 216px;
  display: flex;
  flex-direction: column;
  padding: 4px 0;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.ink};
  box-shadow: 0 10px 26px ${withOpacity(COLORS.ink, 0.16)};
`;

/** A section's label ("Modifiers"): small mono caps over the items it heads. */
const MENU_SECTION_LABEL_STYLE = css`
  label: MENU_SECTION_LABEL_STYLE;
  padding: 6px 14px 5px;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(8.5)};
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${COLORS.midtone};
`;

/** The rule between one section and the next. */
const MENU_RULE_STYLE = css`
  label: MENU_RULE_STYLE;
  height: 1px;
  margin: 7px 14px 1px;
  background: ${COLORS.stroke};
`;

const MENU_ITEM_STYLE = css`
  label: MENU_ITEM_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
  width: 100%;
  padding: 8px 14px;
  border: 0;
  background: transparent;
  color: ${COLORS.ink};
  font-family: ${FONT.body};
  font-size: ${pxToRem(12.5)};
  white-space: nowrap;
  text-align: left;
  cursor: pointer;
  transition: background 120ms ease;

  &:hover { background: ${COLORS.paper}; }
  &:focus-visible { outline: 2px solid ${COLORS.ink}; outline-offset: -2px; }
`;

const DESTRUCTIVE_MENU_ITEM_STYLE = css`
  label: DESTRUCTIVE_MENU_ITEM_STYLE;
  color: ${COLORS.error};
  font-weight: 700;

  &:hover { background: ${COLORS.errorFill}; }
`;

/** A toggle that's on reads in the accent wash. */
const CHECKED_MENU_ITEM_STYLE = css`
  label: CHECKED_MENU_ITEM_STYLE;
  background: ${withOpacity(COLORS.accent, 0.55)};

  &:hover { background: ${withOpacity(COLORS.accent, 0.75)}; }
`;

export type RowKebabMenuItem = {
  label: string;
  onSelect: () => void;
  /** Reads in the error color: deleting or removing something. */
  destructive?: boolean;
  /** A toggle; one that's on reads in the accent wash. */
  checked?: boolean;
  /**
   * The section the item belongs to, labelled over its first item ("Modifiers"). Consecutive items
   * share a section; a rule separates each section from the next. Items without one form an
   * unlabelled section of their own.
   */
  section?: string;
};

/**
 * The actions for one row of a list, behind a kebab that appears on hover. The trigger sits out of
 * flow, pinned to the row's right edge. Its host row spreads KEBAB_HOST_ROW_PROPS onto itself, which
 * reveals the trigger on hover, and slides its trailing cluster left to open a gutter for it
 * (KEBAB_GUTTER_TRANSITION_STYLE + KEBAB_GUTTER_HOVER_STYLE).
 *
 * Renders nothing when there are no items, so a host with nothing to offer keeps its row exactly
 * as it was.
 */
export const RowKebabMenu = ({
  label,
  items,
  alwaysVisible = false,
}: {
  /** The row's own name, used only for the trigger's accessible name ("Actions for {label}"). */
  label: string;
  items: RowKebabMenuItem[];
  /** Skips the hover reveal: the trigger stays visible outright. The host row's own gutter
   *  must be held open to match (KEBAB_GUTTER_OPEN_STYLE), or this sits over whatever the gutter
   *  would otherwise have opened for. */
  alwaysVisible?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    /* Any click that isn't inside this menu closes it, and Escape does too. The row underneath
       is a click target itself, so leaving the menu open over it would arm the wrong thing. */
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  if (items.length === 0) return null;

  const runAction = (action: () => void) => (event: ReactMouseEvent) => {
    event.stopPropagation();
    setOpen(false);
    action();
  };

  return (
    <span css={MENU_WRAP_STYLE} ref={wrapRef}>
      <button
        aria-expanded={open}
        aria-haspopup='menu'
        aria-label={`Actions for ${label}`}
        css={[MENU_TRIGGER_STYLE, KEBAB_TRIGGER_REVEAL_STYLE, alwaysVisible && MENU_TRIGGER_ALWAYS_VISIBLE_STYLE]}
        onClick={event => {
          event.stopPropagation();
          setOpen(previous => !previous);
        }}
        type='button'
      >
        <MoreVerticalIcon size={15} color='currentColor' />
      </button>
      {open
        ? (
          <span css={MENU_STYLE} role='menu'>
            {items.map((item, index) => {
              const startsSection = index === 0 || item.section !== items[index - 1].section;
              return (
                <Fragment key={item.label}>
                  {startsSection && index > 0 && <span css={MENU_RULE_STYLE} role='separator' />}
                  {startsSection && item.section && <span css={MENU_SECTION_LABEL_STYLE}>{item.section}</span>}
                  <button
                    css={[MENU_ITEM_STYLE, item.checked && CHECKED_MENU_ITEM_STYLE, item.destructive && DESTRUCTIVE_MENU_ITEM_STYLE]}
                    onClick={runAction(item.onSelect)}
                    role={item.checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
                    aria-checked={item.checked}
                    type='button'
                  >
                    {item.label}
                  </button>
                </Fragment>
              );
            })}
          </span>
        )
        : null}
    </span>
  );
};
